import { Select } from 'shared/form/components/Select';
import { SelectOption } from 'shared/form/models/SelectOption';
import { useEffect, useMemo } from 'react';
import { Cuenta } from 'core/cuenta/models/Cuenta';
import { useCoreService } from 'core/services/useCoreService';
import { useDataFetcher } from 'shared/http';
import { RegisterOptions, useController } from 'react-hook-form';
import { InputPlaceholder } from 'shared/placeholders';
import { SearchCuentasRequest } from 'core/cuenta/models/SearchCuentasRequests';

interface CuentaSelectorProps {
  label: string;
  name: string;
  registerOptions?: RegisterOptions<any, any>;
  filtros?: SearchCuentasRequest;
  reloadToken?: number;
}

/**
 * Componente selector de Cuentas asíncrono.
 *
 * Actúa como un **Adaptador** entre la UI y el Formulario:
 * - **UI (Visual):** Muestra el objeto completo `{ label, value }` para que el usuario vea el nombre/número.
 * - **Formulario (Datos):** Guarda únicamente el `numero` (number) de la cuenta seleccionada.
 *
 * @component
 * @example
 * // Uso básico dentro de un formulario
 * <CuentaSelector
 * name="cuenta"
 * tipoCuenta={'tipoCuenta'} // Tipo cuenta por el cual se desea filtrar
 * registerOptions={{ required: "Este campo es obligatorio" }}
 * />
 *
 * @param {Object} props - Propiedades del componente.
 * @param {string} props.label - Label que tendrá el campo en el formulario (UI)
 * @param {string} props.name - Nombre del campo en el formulario (key en `data`).
 * @param {RegisterOptions} [props.registerOptions] - Reglas de validación de `react-hook-form` (ej. required, min, etc.).
 * @param {string} props.tipoCuenta - Tipo cuenta por el cual se realiza la búsqueda. Si es null se listan todas las cuentas.
 *
 * @returns {JSX.Element} Un componente Select controlado o un Placeholder de carga.
 */
export const CuentaSelector = ({
  label,
  filtros,
  name,
  registerOptions,
  reloadToken = 0,
}: CuentaSelectorProps): JSX.Element => {
  const [cuentas, setCuentasRequest] = useDataFetcher<SelectOption<Cuenta>[]>([]);
  const coreService = useCoreService();
  const {
    field: { value, onChange },
    fieldState: { invalid },
    formState: { isSubmitted },
  } = useController({
    name,
    rules: registerOptions,
    defaultValue: null,
  });

  const filtrosEstables = useMemo(() => {
    return filtros;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filtros)]);

  useEffect(() => {
    const responseMapper = (lista: Cuenta[]): SelectOption<Cuenta>[] => {
      if (lista == null) {
        return [];
      }
      const result = lista.map((cuenta) => ({
        value: cuenta,
        label: cuenta.label,
      }));
      return result;
    };

    setCuentasRequest(() =>
      coreService
        .getVisibleAccounts(filtrosEstables)
        .then((response) => responseMapper(response)),
    );
  }, [coreService, setCuentasRequest, filtrosEstables, reloadToken]);

  const selectedOption = useMemo(
    () =>
      cuentas.data?.find((option) => `${option.value.numeroCuenta}` === `${value}`) ||
      null,
    [cuentas.data, value],
  );

  const handleSelection = (option: SelectOption<Cuenta>) => {
    onChange(option?.value.numeroCuenta || null);
  };

  if (cuentas.isLoading) {
    return (
      <div className="mt-3 py-1">
        <InputPlaceholder />
      </div>
    );
  }

  return (
    <Select
      name="cuenta_selector"
      label={label}
      options={cuentas.data}
      value={selectedOption}
      onChange={handleSelection}
      classNames={{
        control: () => {
          if (!isSubmitted) {
            return '';
          }
          return invalid ? 'state-error' : 'state-valid';
        },
      }}
    />
  );
};
